import React, { useState, useCallback, useImperativeHandle } from 'react'
import { Withdraw } from './index'
import { OpenWithdrawOptions, WithdrawAPI } from '../types'

// Default configurations
const defaultChains: any[] = []
const defaultMetadata = {}

// Global withdraw manager instance
let globalWithdrawManager: WithdrawAPI | null = null

export const WithdrawManager = React.forwardRef<WithdrawAPI>((_, ref) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [options, setOptions] = useState<OpenWithdrawOptions>({
    chains: [],
    currentActiveAccount: {
      wallet: 'EVM',
      ethAddress: '',
      accountId: '',
      smartWalletAddress: '',
    },
  })

  const openWithdraw = useCallback((newOptions?: OpenWithdrawOptions) => {
    setOptions(
      newOptions || {
        chains: [],
        currentActiveAccount: {
          wallet: 'EVM',
          ethAddress: '',
          accountId: '',
          smartWalletAddress: '',
        },
      }
    )
    setIsOpen(true)
  }, [])

  // Function to update options while modal is open
  const updateWithdrawOptions = useCallback(
    (newOptions: Partial<OpenWithdrawOptions>) => {
      setOptions(prevOptions => ({ ...prevOptions, ...newOptions }))
    },
    []
  )

  const closeWithdraw = useCallback(() => {
    setIsOpen(false)
    setIsLoading(false) // 关闭时清除 loading 状态
    // Call the custom onClose handler if provided
    if (options.onClose) {
      options.onClose()
    }
  }, [options.onClose])

  const setWithdrawLoading = useCallback((loading: boolean) => {
    setIsLoading(loading)
  }, [])

  const isWithdrawOpen = useCallback(() => isOpen, [isOpen])
  const isWithdrawLoading = useCallback(() => isLoading, [isLoading])

  // Expose API through ref
  useImperativeHandle(
    ref,
    () => ({
      openWithdraw,
      closeWithdraw,
      updateWithdrawOptions,
      setWithdrawLoading,
      isOpen: isWithdrawOpen,
      isLoading: isWithdrawLoading,
    }),
    [
      openWithdraw,
      closeWithdraw,
      updateWithdrawOptions,
      setWithdrawLoading,
      isWithdrawOpen,
      isWithdrawLoading,
    ]
  )

  // Register global API
  React.useEffect(() => {
    globalWithdrawManager = {
      openWithdraw,
      closeWithdraw,
      updateWithdrawOptions,
      setWithdrawLoading,
      isOpen: isWithdrawOpen,
      isLoading: isWithdrawLoading,
    }

    return () => {
      globalWithdrawManager = null
    }
  }, [
    openWithdraw,
    closeWithdraw,
    updateWithdrawOptions,
    setWithdrawLoading,
    isWithdrawOpen,
    isWithdrawLoading,
  ])

  if (!isOpen) {
    return null
  }

  return (
    <Withdraw
      isOpen={isOpen}
      isLoading={isLoading}
      onClose={closeWithdraw}
      config={options.config || { symbol: 'USDT' }}
      currentActiveAccount={options.currentActiveAccount}
      chains={options.chains || defaultChains}
      defaultChainId={options.defaultChainId}
      defaultTokenAddress={options.defaultTokenAddress}
      defaultAmount={options.defaultAmount}
      metadata={options.metadata || defaultMetadata}
      currentEquity={options.currentEquity ?? 5000}
      platformBalance={options.platformBalance || '0'} // 传递平台余额
      fee={options.fee} // 传递手续费
      wagmiHooks={options.wagmiHooks}
      onWithdraw={options.onWithdraw}
      onChainChange={options.onChainChange}
      onTokenChange={options.onTokenChange}
      onAmountChanged={options.onAmountChanged}
    />
  )
})

WithdrawManager.displayName = 'WithdrawManager'

// Global API functions
export const openWithdraw = (options?: OpenWithdrawOptions) => {
  if (!globalWithdrawManager) {
    console.error(
      'WithdrawManager not initialized. Make sure to render <WithdrawManager /> in your app.'
    )
    return
  }
  globalWithdrawManager.openWithdraw(
    options || {
      chains: [],
      currentActiveAccount: {
        wallet: 'EVM',
        ethAddress: '',
        accountId: '',
        smartWalletAddress: '',
      },
    }
  )
}

export const closeWithdraw = () => {
  if (!globalWithdrawManager) {
    console.error(
      'WithdrawManager not initialized. Make sure to render <WithdrawManager /> in your app.'
    )
    return
  }
  globalWithdrawManager.closeWithdraw()
}

export const updateWithdrawOptions = (
  options: Partial<OpenWithdrawOptions>
) => {
  if (!globalWithdrawManager) {
    console.error(
      'WithdrawManager not initialized. Make sure to render <WithdrawManager /> in your app.'
    )
    return
  }
  globalWithdrawManager.updateWithdrawOptions(options)
}

export const setWithdrawLoading = (loading: boolean) => {
  if (!globalWithdrawManager) {
    console.error(
      'WithdrawManager not initialized. Make sure to render <WithdrawManager /> in your app.'
    )
    return
  }
  globalWithdrawManager.setWithdrawLoading(loading)
}

export const isWithdrawOpen = () => {
  if (!globalWithdrawManager) {
    return false
  }
  return globalWithdrawManager.isOpen()
}

export const isWithdrawLoading = () => {
  if (!globalWithdrawManager) {
    return false
  }
  return globalWithdrawManager.isLoading()
}

// Hook for using withdraw API in React components
export const useWithdraw = (): WithdrawAPI => {
  return {
    openWithdraw,
    closeWithdraw,
    updateWithdrawOptions,
    setWithdrawLoading,
    isOpen: isWithdrawOpen,
    isLoading: isWithdrawLoading,
  }
}
